import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { useLocalization } from "@/hooks/useLocalization";
import { useDebounce } from "@/hooks/useDebounce";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, BookOpen, Clock, Tag, ExternalLink, Globe } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { APP_LANGUAGE_DEFINITIONS } from "@/localization/languages";

type PortalArticle = Tables<'portal_articles'>;

const ARTICLE_CATEGORIES = [
  "first_fitting",
  "undergarments_foundation", 
  "transport_care",
  "wedding_day_preparation",
  "alterations_process",
  "general_faq"
];

const SUPPORTED_LANGUAGES = APP_LANGUAGE_DEFINITIONS.map((language) => ({
  code: language.code,
  name: language.nativeName,
}));

export default function CustomerHelp() {
  const { customer, loading: authLoading } = useCustomerAuth();
  const { t } = useLocalization();
  const { toast } = useToast();
  const [articles, setArticles] = useState<PortalArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (authLoading) return;
    
    if (!customer) {
      toast({
        title: "Authentication required",
        description: "Please log in to view help articles.",
        variant: "destructive",
      });
      return;
    }

    loadArticles();
  }, [authLoading, customer]);

  const loadArticles = async () => {
    try {
      if (!customer) return;

      const { data, error } = await supabase.functions.invoke('customer-portal-articles', {
        body: { 
          customer_email: customer.email,
          customer_id: customer.id,
          session_token: 'customer_session' // Basic session token for validation
        }
      });

      if (error) throw error;
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast({
        title: "Error loading articles",
        description: "Failed to load help articles. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = !debouncedSearchQuery || 
      article.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      (article.excerpt && article.excerpt.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));

    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    const matchesLanguage = article.locale === selectedLanguage || (!article.locale && selectedLanguage === 'en');

    return matchesSearch && matchesCategory && matchesLanguage;
  });

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800">$1</mark>');
  };

  const groupedArticles = ARTICLE_CATEGORIES.reduce((acc, category) => {
    acc[category] = filteredArticles.filter(article => article.category === category);
    return acc;
  }, {} as Record<string, PortalArticle[]>);

  // Add uncategorized articles
  const uncategorized = filteredArticles.filter(article => 
    !article.category || !ARTICLE_CATEGORIES.includes(article.category)
  );
  if (uncategorized.length > 0) {
    groupedArticles['other'] = uncategorized;
  }

  if (authLoading || loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="text-center space-y-3 sm:space-y-4 px-2">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">{t('help_tips')}</h1>
        <p className="text-muted-foreground text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
          {t('help_description')}
        </p>
      </div>

      {/* Search and Language Selector */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-2xl mx-auto">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search_articles')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 sm:h-10 text-base sm:text-sm"
          />
        </div>
        <div className="flex items-center gap-2 sm:min-w-[200px]">
          <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger className="h-12 sm:h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Filter - Horizontal Scrollable and Sticky */}
      <div className="sticky top-14 sm:top-16 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border py-3 sm:py-4 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4">
        <ScrollArea className="w-full whitespace-nowrap overflow-x-auto">
          <div className="flex gap-2 pb-2">
            <Badge
              variant={selectedCategory === null ? "default" : "outline"}
              className="cursor-pointer transition-colors shrink-0 min-h-[36px] sm:min-h-[32px] px-3 py-2 text-sm"
              onClick={() => setSelectedCategory(null)}
            >
              {t('all_categories')}
            </Badge>
            {ARTICLE_CATEGORIES.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="cursor-pointer transition-colors shrink-0 min-h-[36px] sm:min-h-[32px] px-3 py-2 text-sm"
                onClick={() => setSelectedCategory(category)}
              >
                {t(category as keyof typeof t)}
              </Badge>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Search Results Info */}
      {(searchQuery || selectedCategory) && (
        <div className="text-center text-sm text-muted-foreground">
          {filteredArticles.length} {filteredArticles.length === 1 ? t('article_found') : t('articles_found')}
          {searchQuery && ` for "${searchQuery}"`}
          {selectedCategory && ` in ${t(selectedCategory as keyof typeof t)}`}
        </div>
      )}

      {/* Articles by Category */}
      <div className="space-y-8 sm:space-y-12">
        {Object.entries(groupedArticles).map(([category, categoryArticles]) => {
          if (categoryArticles.length === 0) return null;

          return (
            <div key={category} className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-2 sm:gap-3 px-1">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground">{t(category as keyof typeof t)}</h2>
                <Badge variant="secondary" className="text-xs">{categoryArticles.length}</Badge>
              </div>

              <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} searchQuery={debouncedSearchQuery} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('no_articles_found')}</h3>
          <p className="text-muted-foreground">
            {debouncedSearchQuery || selectedCategory 
              ? t('adjust_search')
              : t('no_articles_available')}
          </p>
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article, searchQuery }: { article: PortalArticle; searchQuery: string }) {
  const { t } = useLocalization();
  return (
    <Card className="h-full hover:shadow-md transition-all duration-200 group active:scale-98">
      <CardHeader className="pb-3 p-4 sm:p-6">
        <CardTitle className="text-base sm:text-lg line-clamp-2 group-hover:text-primary transition-colors leading-tight">
          <span dangerouslySetInnerHTML={{ 
            __html: highlightText(article.title, searchQuery) 
          }} />
        </CardTitle>
        {article.excerpt && (
          <CardDescription className="line-clamp-3 text-sm leading-relaxed">
            <span dangerouslySetInnerHTML={{ 
              __html: highlightText(article.excerpt, searchQuery) 
            }} />
          </CardDescription>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
          {article.published_at && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 flex-shrink-0" />
              <span>{new Date(article.published_at).toLocaleDateString()}</span>
            </div>
          )}
          {article.tags && article.tags.length > 0 && (
            <div className="flex items-center gap-1 min-w-0">
              <Tag className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{article.tags.slice(0, 2).join(", ")}</span>
              {article.tags.length > 2 && (
                <span className="text-muted-foreground/70">+{article.tags.length - 2}</span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 p-4 sm:p-6 sm:pt-0">
        <Link 
          to={`/help/${article.slug}`}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors min-h-[44px] sm:min-h-auto py-2 sm:py-0"
        >
          {t('read_full_article')}
          <ExternalLink className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );

  function highlightText(text: string, query: string) {
    if (!query) return DOMPurify.sanitize(text);
    const safeText = DOMPurify.sanitize(text);
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return safeText.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 rounded px-1">$1</mark>');
  }
}
